import { SaveManager } from '../../../../state/SaveManager.js';
import { STORY_FOREST_ASSETS } from '../StoryForestAssets.js';
import { resolveStoryForestLayout } from '../components/StoryForestLayout.js';
import type { StoryTrailConfig } from './StoryTrailConfig.js';
import { StoryTrailState } from './StoryTrailState.js';

const FONT_READING = '"Nunito", Arial, sans-serif';

interface StoryTrailOptions {
  state?: StoryTrailState;
  reducedMotion: boolean;
  onComplete: (config: StoryTrailConfig) => void;
  onExit: () => void;
}

interface EventTokenView {
  id: string;
  container: Phaser.GameObjects.Container;
  image: Phaser.GameObjects.Image;
  caption: Phaser.GameObjects.Text;
}

interface EventSlotView {
  container: Phaser.GameObjects.Container;
  image: Phaser.GameObjects.Image;
  number: Phaser.GameObjects.Text;
  caption: Phaser.GameObjects.Text;
}

interface TextButtonView {
  container: Phaser.GameObjects.Container;
  text: Phaser.GameObjects.Text;
}

export class StoryTrail {
  readonly state: StoryTrailState;

  private readonly root: Phaser.GameObjects.Container;
  private readonly panel: Phaser.GameObjects.Image;
  private readonly titleText: Phaser.GameObjects.Text;
  private readonly passageText: Phaser.GameObjects.Text;
  private readonly instructionText: Phaser.GameObjects.Text;
  private readonly feedbackText: Phaser.GameObjects.Text;
  private readonly controlsText: Phaser.GameObjects.Text;
  private readonly eventTokens: EventTokenView[];
  private readonly slots: EventSlotView[];
  private readonly checkButton: TextButtonView;
  private readonly hintButton: TextButtonView;
  private readonly resetButton: TextButtonView;
  private readonly closeButton: TextButtonView;
  private readonly successVfx: Phaser.GameObjects.Image;
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private selectedEventId: string | null = null;
  private focusedSlot = 0;
  private hintStage = 0;
  private markerSize = 92;
  private suspended = false;
  private completionHandled = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly config: StoryTrailConfig,
    private readonly options: StoryTrailOptions,
  ) {
    this.state = options.state ?? new StoryTrailState(config);
    this.root = scene.add.container(0, 0).setDepth(200);
    this.panel = scene.add.image(0, 0, STORY_FOREST_ASSETS.storyMissionPanel.key);
    this.titleText = this.makeText(config.title, 25, '#fff8e7', 'bold')
      .setStroke('#54246f', 6);
    this.passageText = this.makeText(config.passage, 19, '#3d2a31', 'normal');
    this.instructionText = this.makeText(
      'Restore the trail from first to last.',
      16,
      '#63377d',
      'bold',
    );
    this.feedbackText = this.makeText('', 16, '#fff8e7', 'bold')
      .setStroke('#54246f', 5);
    this.controlsText = this.makeText(
      '1–9 select  •  ← → choose a place  •  Enter place  •  C check',
      12,
      '#fff8e7',
      'normal',
    ).setStroke('#54246f', 4);
    this.slots = config.events.map((_event, index) => this.createSlot(index));
    this.eventTokens = config.events.map((event, index) => this.createToken(event.id, event.text, index));
    this.checkButton = this.createTextButton('✦  LIGHT THE TRAIL', () => this.checkOrder());
    this.hintButton = this.createTextButton('?  STORY HINT', () => this.showHint());
    this.resetButton = this.createTextButton('↺  UNTANGLE', () => this.resetOrder());
    this.closeButton = this.createTextButton('←  BACK', () => this.options.onExit());
    this.successVfx = scene.add.image(0, 0, STORY_FOREST_ASSETS.storyMagicVfx.key)
      .setVisible(false)
      .setAlpha(0);

    this.root.add([
      this.panel,
      this.titleText,
      this.passageText,
      this.instructionText,
      ...this.slots.map(slot => slot.container),
      ...this.eventTokens.map(token => token.container),
      this.feedbackText,
      this.controlsText,
      this.checkButton.container,
      this.hintButton.container,
      this.resetButton.container,
      this.closeButton.container,
      this.successVfx,
    ]);

    scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
    this.layout();
    this.refreshViews();
  }

  layout() {
    const { safe, compact } = resolveStoryForestLayout(this.scene);
    const panelWidth = Math.min(compact ? 900 : 940, safe.width - 18);
    const panelHeight = Math.min(safe.height - 10, panelWidth * (1086 / 1448));
    const panelY = safe.centerY;
    this.panel.setPosition(safe.centerX, panelY).setDisplaySize(panelWidth, panelHeight);
    this.closeButton.container.setPosition(safe.x + (compact ? 76 : 88), safe.y + 48);
    this.closeButton.text.setFontSize(compact ? 15 : 16);
    this.titleText.setPosition(safe.centerX, safe.y + (compact ? 157 : 165)).setFontSize(compact ? 23 : 26);
    this.passageText.setPosition(safe.centerX, safe.y + (compact ? 242 : 236))
      .setFontSize(compact ? 18 : 19)
      .setWordWrapWidth(Math.min(compact ? 640 : 690, panelWidth - 180));
    this.instructionText.setPosition(safe.centerX, safe.y + (compact ? 300 : 304))
      .setFontSize(compact ? 15 : 16);

    const count = this.slots.length;
    const usableWidth = Math.min(panelWidth - 150, compact ? 700 : 750);
    const step = usableWidth / count;
    const startX = safe.centerX - usableWidth * 0.5 + step * 0.5;
    const slotY = safe.y + (compact ? 350 : 354);
    const tokenY = safe.y + (compact ? 452 : 454);
    const markerSize = compact ? 68 : 70;
    this.markerSize = markerSize;
    const captionWidth = Math.max(170, step - 18);

    this.slots.forEach((slot, index) => {
      const trailOffset = index % 2 === 0 ? -7 : 10;
      slot.container.setPosition(startX + step * index, slotY + trailOffset);
      slot.image.setDisplaySize(markerSize, markerSize);
      slot.number.setFontSize(compact ? 17 : 19);
      slot.caption.setPosition(0, markerSize * 0.55)
        .setFontSize(compact ? 15 : 16)
        .setWordWrapWidth(captionWidth);
    });
    this.eventTokens.forEach((token, index) => {
      token.container.setPosition(startX + step * index, tokenY);
      token.image.setDisplaySize(markerSize, markerSize);
      token.caption.setPosition(0, markerSize * 0.55)
        .setFontSize(compact ? 15 : 16)
        .setWordWrapWidth(captionWidth);
    });

    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 128 : 130))
      .setFontSize(compact ? 15 : 16)
      .setWordWrapWidth(Math.min(720, panelWidth - 250));
    this.controlsText.setPosition(safe.centerX, safe.bottom - (compact ? 94 : 96))
      .setFontSize(compact ? 11 : 12);
    const buttonY = safe.bottom - 45;
    const buttonGap = compact ? 175 : 190;
    this.checkButton.container.setPosition(safe.centerX - buttonGap, buttonY);
    this.hintButton.container.setPosition(safe.centerX, buttonY);
    this.resetButton.container.setPosition(safe.centerX + buttonGap, buttonY);
    [this.checkButton, this.hintButton, this.resetButton].forEach(button => {
      button.text.setFontSize(compact ? 15 : 16);
    });
    this.successVfx.setPosition(safe.centerX, slotY + 70)
      .setDisplaySize(compact ? 350 : 420, compact ? 350 : 420);
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  destroy() {
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.timers.forEach(timer => timer.remove(false));
    this.timers.length = 0;
    this.eventTokens.forEach(token => this.scene.tweens.killTweensOf(token.container));
    this.scene.tweens.killTweensOf(this.successVfx);
    this.root.destroy(true);
  }

  private makeText(
    value: string,
    size: number,
    color: string,
    fontStyle: 'normal' | 'bold',
  ) {
    return this.scene.add.text(0, 0, value, {
      fontFamily: FONT_READING,
      fontSize: `${size}px`,
      fontStyle,
      color,
      align: 'center',
      lineSpacing: 4,
    }).setOrigin(0.5).setResolution(2);
  }

  private createSlot(index: number): EventSlotView {
    const container = this.scene.add.container(0, 0);
    const image = this.scene.add.image(0, 0, STORY_FOREST_ASSETS.storyEventSlot.key)
      .setInteractive({ useHandCursor: true });
    const number = this.makeText(`${index + 1}`, 19, '#fff8df', 'bold')
      .setStroke('#45205f', 5);
    const caption = this.makeText('', 16, '#3d2a31', 'bold').setOrigin(0.5, 0);
    image.on('pointerup', () => this.activateSlot(index));
    container.add([image, number, caption]);
    return { container, image, number, caption };
  }

  private createToken(id: string, label: string, index: number): EventTokenView {
    const container = this.scene.add.container(0, 0);
    const image = this.scene.add.image(0, 0, STORY_FOREST_ASSETS.storyEventToken.key)
      .setInteractive({ useHandCursor: true });
    const caption = this.makeText(label, 16, '#3d2a31', 'bold').setOrigin(0.5, 0);
    image.on('pointerup', () => this.selectToken(id));
    container.add([image, caption]);
    container.setData('numberKey', index + 1);
    return { id, container, image, caption };
  }

  private createTextButton(label: string, action: () => void): TextButtonView {
    const container = this.scene.add.container(0, 0);
    const hitArea = this.scene.add.zone(0, 0, 176, 46).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 16, '#fff8e7', 'bold').setStroke('#54246f', 5);
    hitArea.on('pointerdown', () => text.setScale(0.96).setAlpha(0.78));
    hitArea.on('pointerout', () => text.setScale(1).setAlpha(1));
    hitArea.on('pointerup', () => {
      text.setScale(1).setAlpha(1);
      if (!this.suspended && !this.completionHandled) action();
    });
    container.add([hitArea, text]);
    return { container, text };
  }

  private selectToken(eventId: string) {
    if (this.suspended || this.completionHandled) return;
    this.selectedEventId = eventId;
    this.feedbackText.setText('Choose a place on the trail.');
    this.refreshViews();
  }

  private activateSlot(slotIndex: number) {
    if (this.suspended || this.completionHandled) return;
    this.focusedSlot = slotIndex;
    if (this.selectedEventId) {
      this.state.place(this.selectedEventId, slotIndex);
      this.selectedEventId = null;
    } else {
      const placedId = this.state.placedOrder[slotIndex];
      if (placedId) {
        this.state.remove(slotIndex);
        this.selectedEventId = placedId;
      }
    }
    this.feedbackText.setText('');
    this.refreshViews();
  }

  private resetOrder() {
    if (!this.state.resetOrder()) return;
    this.selectedEventId = null;
    this.hintStage = 0;
    this.feedbackText.setText('The moments are ready for a new trail.');
    this.refreshViews();
  }

  private showHint() {
    const incorrectSlot = this.state.firstIncorrectSlot();
    this.focusedSlot = Math.max(0, incorrectSlot);
    const firstEvent = this.config.events.find(event => event.id === this.config.correctOrder[0]);
    const lastEvent = this.config.events.find(event => event.id === this.config.correctOrder.at(-1));
    const hints = [
      this.config.hint,
      firstEvent ? `Start with this moment: ${firstEvent.text}` : this.config.hint,
      lastEvent ? `The final moment is: ${lastEvent.text}` : this.config.hint,
    ];
    this.feedbackText.setText(hints[Math.min(this.hintStage, hints.length - 1)]);
    this.hintStage += 1;
    this.refreshViews();
  }

  private checkOrder() {
    const result = this.state.check();
    if (result === 'incomplete') {
      this.feedbackText.setText('Every story moment needs a place on the trail.');
      return;
    }
    if (result === 'incorrect') {
      SaveManager.recordAnswer(this.config.skillId, false);
      this.feedbackText.setText('Almost! Think about what happened first. Your trail is still here.');
      this.focusedSlot = Math.max(0, this.state.firstIncorrectSlot());
      this.hintStage = Math.max(1, this.hintStage);
      this.refreshViews();
      return;
    }
    this.complete();
  }

  private complete() {
    if (this.completionHandled) return;
    this.completionHandled = true;
    SaveManager.recordAnswer(this.config.skillId, true);
    this.feedbackText.setText(this.config.successMessage);
    const targetScaleX = this.successVfx.scaleX;
    const targetScaleY = this.successVfx.scaleY;
    this.successVfx.setVisible(true).setAlpha(0);
    if (!this.options.reducedMotion) {
      this.successVfx.setScale(targetScaleX * 0.82, targetScaleY * 0.82);
    }
    this.scene.tweens.add({
      targets: this.successVfx,
      alpha: 1,
      scaleX: targetScaleX,
      scaleY: targetScaleY,
      duration: this.options.reducedMotion ? 180 : 520,
      ease: 'Sine.easeOut',
    });
    const timer = this.scene.time.delayedCall(this.options.reducedMotion ? 650 : 1150, () => {
      this.options.onComplete(this.config);
    });
    this.timers.push(timer);
  }

  private refreshViews() {
    const placed = this.state.placedOrder;
    const collected = this.state.collectedEventIds;
    this.eventTokens.forEach(token => {
      const isPlaced = placed.includes(token.id);
      token.container.setVisible(!isPlaced && collected.includes(token.id));
      token.image.clearTint().setAlpha(1).setDisplaySize(this.markerSize, this.markerSize);
      if (this.selectedEventId === token.id) {
        token.image.setTint(0xffdc73)
          .setDisplaySize(this.markerSize * 1.08, this.markerSize * 1.08);
      }
    });
    this.slots.forEach((slot, index) => {
      const event = this.state.eventForId(placed[index]);
      const focused = index === this.focusedSlot;
      slot.caption.setText(event?.text ?? this.slotLabel(index));
      const slotSize = this.markerSize * (focused ? 1.08 : 1);
      slot.image.setTexture(
        focused
          ? STORY_FOREST_ASSETS.storyEventSlotActive.key
          : STORY_FOREST_ASSETS.storyEventSlot.key,
      ).clearTint().setAlpha(event || focused ? 1 : 0.82).setDisplaySize(slotSize, slotSize);
      slot.number.setAlpha(event ? 0.86 : 1);
    });
  }

  private slotLabel(index: number) {
    if (index === 0) return 'First';
    if (index === this.slots.length - 1) return 'Last';
    return `Then ${index + 1}`;
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (this.suspended || this.completionHandled) return;
    if (event.key >= '1' && event.key <= '9') {
      const token = this.eventTokens[Number(event.key) - 1];
      if (token) this.selectToken(token.id);
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      this.focusedSlot = Phaser.Math.Wrap(this.focusedSlot + direction, 0, this.slots.length);
      this.refreshViews();
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      this.activateSlot(this.focusedSlot);
      event.preventDefault();
      return;
    }
    if (event.key.toLowerCase() === 'c') this.checkOrder();
    if (event.key.toLowerCase() === 'h') this.showHint();
    if (event.key.toLowerCase() === 'r') this.resetOrder();
    if (event.key === 'Escape') this.options.onExit();
  }
}
